import { Loader, Sparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import frIcon from "@/assets/fr-icon.svg";
import enIcon from "@/assets/en-icon.svg";
import { Button } from "@/components/ui/button";
import { Field, FieldLabel } from "@/components/ui/field";
import { Textarea } from "@/components/ui/textarea";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { useThemesSearch } from "@/features/themes/hooks/useThemesSearch";
import type { ApiTheme } from "@/features/themes/types";
import { cn } from "@/lib/utils";
import { catchError } from "@/lib/errors";
import { handleApiError } from "@/utils/errors";
import type { ApiDilemma } from "@/features/dilemmas/types";
import { useGenerateDilemma } from "../hooks/useGenerateDilemma";

const IDEA_MAX_LENGTH = 1000;

interface AiWritingAssistantInputViewProps {
  selectedTheme: ApiTheme | null;
  setSelectedTheme: (theme: ApiTheme | null) => void;
  idea: string;
  setIdea: (idea: string) => void;
  sourceLanguage: "fr" | "en";
  setSourceLanguage: (language: "fr" | "en") => void;
  onCancel: () => void;
  onGenerated: (dilemma: ApiDilemma) => void;
}

const AiWritingAssistantInputView = ({
  selectedTheme,
  setSelectedTheme,
  idea,
  setIdea,
  sourceLanguage,
  setSourceLanguage,
  onCancel,
  onGenerated,
}: AiWritingAssistantInputViewProps) => {
  const { t } = useTranslation();
  const { search, setSearch, themes, isLoading } = useThemesSearch();
  const { generate, isPending } = useGenerateDilemma();

  const canGenerate = !!selectedTheme || idea.trim().length > 0;

  async function handleGenerate() {
    if (!canGenerate) return;
    const [error, dilemma] = await catchError(
      generate({
        source_language: sourceLanguage,
        topic_id: selectedTheme?.id,
        description: idea.trim() || undefined,
      }),
    );
    if (error || !dilemma) {
      toast.error(
        error
          ? handleApiError(error).message
          : t("modules.dilemmas.ai_assistant.generation_error"),
      );
      return;
    }
    onGenerated(dilemma);
  }

  return (
    <div className="flex flex-col gap-4">
      <Field>
        <FieldLabel>
          {t("modules.dilemmas.ai_assistant.language_field_label")}
        </FieldLabel>
        <div className="flex items-center gap-3">
          <button
            type="button"
            disabled={isPending}
            onClick={() => setSourceLanguage("fr")}
            className={cn(
              "cursor-pointer flex flex-1 lg:flex-none items-center gap-3 p-3 py-2 w-36 rounded-md border border-transparent bg-gray-100 text-gray-500",
              sourceLanguage === "fr" && "bg-white text-primary border-primary",
              isPending && "opacity-50 cursor-not-allowed",
            )}
          >
            <img src={frIcon} alt="FR" className="size-5" />
            {t("languages.fr")}
          </button>
          <button
            type="button"
            disabled={isPending}
            onClick={() => setSourceLanguage("en")}
            className={cn(
              "cursor-pointer flex flex-1 lg:flex-none items-center gap-3 p-3 py-2 w-36 rounded-md border border-transparent bg-gray-100 text-gray-500",
              sourceLanguage === "en" && "bg-white text-primary border-primary",
              isPending && "opacity-50 cursor-not-allowed",
            )}
          >
            <img src={enIcon} alt="EN" className="size-5" />
            {t("languages.en")}
          </button>
        </div>
      </Field>

      <Field>
        <FieldLabel htmlFor="ai-assistant-topic">
          {t("modules.dilemmas.ai_assistant.theme_field_label")}
        </FieldLabel>
        <Combobox
          items={themes}
          value={selectedTheme}
          onValueChange={setSelectedTheme}
          inputValue={search}
          onInputValueChange={setSearch}
          filter={null}
          itemToStringLabel={(theme: ApiTheme) => theme?.name ?? ""}
          isItemEqualToValue={(a: ApiTheme, b: ApiTheme) => a?.id === b?.id}
        >
          <ComboboxInput
            id="ai-assistant-topic"
            showClear
            placeholder={t(
              "modules.dilemmas.ai_assistant.theme_field_placeholder",
            )}
            className="h-10 overflow-hidden"
            disabled={isPending}
          />
          <ComboboxContent className="pointer-events-auto">
            <ComboboxEmpty>
              {isLoading
                ? t("keys.loading")
                : t("modules.dilemmas.ai_assistant.no_theme_found")}
            </ComboboxEmpty>
            <ComboboxList>
              {(theme: ApiTheme) => (
                <ComboboxItem key={theme.id} value={theme}>
                  {theme.name}
                </ComboboxItem>
              )}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
      </Field>

      <p className="text-center text-xs text-muted-foreground">
        {t("modules.dilemmas.ai_assistant.or_separator")}
      </p>

      <Field>
        <FieldLabel htmlFor="ai-assistant-idea">
          {t("modules.dilemmas.ai_assistant.idea_field_label")}
        </FieldLabel>
        <Textarea
          id="ai-assistant-idea"
          rows={4}
          value={idea}
          maxLength={IDEA_MAX_LENGTH}
          onChange={(e) => setIdea(e.target.value)}
          placeholder={t("modules.dilemmas.ai_assistant.idea_placeholder")}
          disabled={isPending}
        />
      </Field>

      <div className="flex items-center justify-end gap-3 pt-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onCancel}
          disabled={isPending}
        >
          {t("keys.cancel")}
        </Button>
        <Button
          type="button"
          onClick={handleGenerate}
          disabled={isPending || !canGenerate}
        >
          {isPending ? (
            <Loader className="animate-spin" />
          ) : (
            <Sparkles className="size-4" />
          )}
          {t("modules.dilemmas.ai_assistant.generate_button")}
        </Button>
      </div>
    </div>
  );
};

export default AiWritingAssistantInputView;
